import { Alert, Button, CopyButton, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import prettifiedLabel from '../../constants/labelPrettifier';

// Shopify expires a charge the merchant never approved after two days, so a tab
// left open on this page outlives the link it is showing.
const EXPIRY_RECHECK_MS = 60000;

const localDateTime = (iso) => DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED, { locale: 'en-US' });

// Same formats as ActivateDomainSubscriptionCard, which is where a merchant saw
// these numbers when they picked the plan.
const planPrice = (price, currency) => parseFloat(price || 0).toLocaleString('en-US', { style: 'currency', currency });

const unitPrice = (price, currency) =>
  parseFloat(price || 0).toLocaleString('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });

const planTerms = (subscription) => {
  const applied = subscription.appliedValues;
  const base =
    applied?.subscriptionTieredPricings?.map((p, i) => {
      const price = `${planPrice(p.price, applied.currency)} / month`;
      const threshold = parseInt(p.treshold || 0).toLocaleString('en-US');
      if (i > 0) {
        return `${price} @ ${threshold} ${p.unit}+`;
      }
      return p.treshold > 0 ? `${price} (up to ${threshold} ${p.unit})` : price;
    }) ?? [];
  const usage =
    applied?.subscriptionDynamicPricings?.map((p) =>
      [unitPrice(p.unitPrice, applied.currency), p.unit && `/ ${prettifiedLabel(p.unit.slice(0, -1))}`]
        .filter(Boolean)
        .join(' ')
    ) ?? [];

  const prices = [base.join(', '), usage.length > 0 && `usage: ${usage.join(', ')}`].filter(Boolean).join(' + ');

  return [applied?.name, prices].filter(Boolean).join(' — ');
};

const PendingSubscriptionChangeAlert = ({ domainSubscriptions }) => {
  const [now, setNow] = useState(() => Date.now());

  const pending = domainSubscriptions.filter((s) => s.status === 'pendingConfirmation');
  // One confirmation creates every pending row and hands them all the same link,
  // so the first row carrying one speaks for the whole change.
  const linked = pending.find((s) => s.confirmationUrl);
  const expiresAt = linked?.confirmationExpiresAt;
  const expired = expiresAt ? DateTime.fromISO(expiresAt).toMillis() <= now : false;

  useEffect(() => {
    if (!expiresAt || expired) {
      return undefined;
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), EXPIRY_RECHECK_MS);
    return () => clearInterval(timer);
  }, [expiresAt, expired]);

  if (pending.length === 0) {
    return null;
  }

  const actionable = !!linked && !expired;

  let expiryNote;
  if (!linked) {
    expiryNote =
      'The approval link is not available for this change. Choose or customize the plan again to generate a new one.';
  } else if (expired) {
    expiryNote = `The link expired on ${localDateTime(expiresAt)}. Choose or customize the plan again to generate a new one.`;
  } else if (expiresAt) {
    const relative = DateTime.fromISO(expiresAt).toRelative({ base: DateTime.fromMillis(now), locale: 'en-US' });
    expiryNote = `The link expires on ${localDateTime(expiresAt)} (${relative}).`;
  }

  const requested = [
    pending[0].approvedBy && `by ${pending[0].approvedBy}`,
    pending[0].createdAt && `on ${localDateTime(pending[0].createdAt)}`,
  ].filter(Boolean);

  const linkProps = actionable
    ? { component: 'a', href: linked.confirmationUrl, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Alert
      color={actionable ? 'yellow' : 'red'}
      icon={<IconAlertTriangle size={18} />}
      title="Subscription change awaiting approval"
      mb="md"
    >
      <Stack gap="xs" align="flex-start">
        {pending.map((s, i) => (
          <Text size="sm" key={s.id ?? i}>
            {planTerms(s)}
          </Text>
        ))}
        {requested.length > 0 && (
          <Text size="sm" c="dimmed">
            Requested {requested.join(' ')}.
          </Text>
        )}
        <Text size="sm">
          The change only takes effect once the merchant approves it in their store through the approval link.
        </Text>
        {expiryNote && <Text size="sm">{expiryNote}</Text>}
        <Group gap="xs">
          <CopyButton value={linked?.confirmationUrl}>
            {({ copied, copy }) => (
              <Button size="xs" variant="default" onClick={copy} disabled={!actionable}>
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            )}
          </CopyButton>
          <Button
            size="xs"
            variant="default"
            disabled={!actionable}
            leftSection={<IconExternalLink size={14} />}
            {...linkProps}
          >
            Open in new tab
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
};

export default PendingSubscriptionChangeAlert;
